"use client"

import { useState } from "react"
import { Sparkles, Briefcase } from "lucide-react"

import { TextAction } from "@/components/graphite/text-action"
import { JobSelectorDialog } from "@/components/job-selector-dialog"
//import { useJobAssignments } from "@/contexts/job-assignments-context"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { useGetJobs, usePostJobsJobIdPipelineAdd } from "openapi/api/endpoints"

interface JobSearchBulkActionsProps {
  selectedCount: number
  selectedCandidates: string[]
  onBulkAction: (action: string) => void
  onStartOutreach: () => void
  /** 27a puts a way out of a selection beside the things it can do. */
  onClear?: () => void
}

export function JobSearchBulkActions({
  selectedCount,
  selectedCandidates,
  onBulkAction,
  onStartOutreach,
  onClear,
}: JobSearchBulkActionsProps) {
  const [isJobSelectorOpen, setIsJobSelectorOpen] = useState(false)
  //const { availableJobs, assignCandidatesToJob } = useJobAssignments()
  const { mutateAsync: addToJob } = usePostJobsJobIdPipelineAdd()
  const { data:availableJobs } = useGetJobs()
  const queryClient = useQueryClient()


  if (selectedCount === 0) {
    return null
  }

  const handleAssignToJob = async (jobId: string, stage: string) => {
    try {
      /*
        The endpoint answers 207 as well as 201: some of the selected candidates
        can be listed under `failures` while the request itself resolves. This
        used to toast "Candidate added to job successfully" over a response
        saying the opposite.
      */
      const response = (await addToJob({
        jobId,
        data: { candidateIds: selectedCandidates, stage },
      })) as { failures?: { candidateId?: string; error?: string }[] } | undefined

      const failures = response?.failures ?? []
      const added = selectedCandidates.length - failures.length

      /*
        Both keys: the candidates list, and the jobs query the results page
        reads to know who is already on the pipeline. Invalidating only
        `/candidates` left every row still offering to add someone it had just
        added.
      */
      await queryClient.invalidateQueries({ queryKey: ["/candidates"], exact: false })
      await queryClient.invalidateQueries({ queryKey: ["/jobs"], exact: false })

      if (failures.length > 0) {
        toast.warning(
          added > 0
            ? `${added} of ${selectedCandidates.length} added`
            : "None of them could be added",
          { description: failures[0]?.error ?? "The pipeline reported failures." },
        )
      } else {
        toast.success(
          `${added} candidate${added === 1 ? "" : "s"} added to the job`,
        )
      }

      onBulkAction("assign_job")
      setIsJobSelectorOpen(false)
    } catch (error) {
      toast.error("Could not add to the job", { description: `${error}` })
    }
  }

  return (
    <>
      {/*
        27a's selection bar: a strip on the results, not a card floating over
        them, with the teal edge that marks the selection itself.

        Add Tags and Move Stage were here and are not any more. Both called
        `onBulkAction` with a string the results page does not handle, so they
        had never done anything — a control that cannot act is worse than an
        absent one. Add to Job is the working action and keeps its dialog.
      */}
      <div className="flex flex-none flex-wrap items-center gap-x-5 gap-y-2 border-b bg-foreground/[0.03] px-4 py-2.5 shadow-[inset_2px_0_0_var(--primary)] @2xl:px-[18px]">
        <span className="text-[12px] font-medium leading-none">
          {selectedCount} selected
        </span>
        <span className="ml-auto flex items-center gap-5">
          <TextAction
            tone="teal"
            onClick={() => setIsJobSelectorOpen(true)}
            className="text-[11.5px]"
          >
            <Briefcase className="h-3 w-3" />
            Add to Job
          </TextAction>
          <TextAction onClick={onStartOutreach} className="text-[11.5px]">
            <Sparkles className="h-3 w-3" />
            Start outreach
          </TextAction>
          {onClear && (
            <TextAction onClick={onClear} className="text-[11.5px]">
              Clear
            </TextAction>
          )}
        </span>
      </div>

      <JobSelectorDialog
        isOpen={isJobSelectorOpen}
        onClose={() => setIsJobSelectorOpen(false)}
        onAssign={handleAssignToJob}
        availableJobs={availableJobs??[]}
        selectedCount={selectedCount}
      />
    </>
  )
}
